import { useState } from 'react';
import { RefreshCw, Database, Clock, AlertCircle, CheckCircle } from 'lucide-react';
import { useSources, useTriggerFetch, useTriggerFetchAll } from '../hooks/useLeads';
import { Button } from '../components/common/Button';
import type { DataSource } from '../api/client';

export function Settings() {
  const { data: sourcesData, isLoading } = useSources();
  const triggerFetch = useTriggerFetch();
  const triggerFetchAll = useTriggerFetchAll();

  const [fetchingSourceId, setFetchingSourceId] = useState<number | null>(null);

  const handleFetchSource = async (sourceId: number) => {
    setFetchingSourceId(sourceId);
    try {
      await triggerFetch.mutateAsync(sourceId);
    } finally {
      setFetchingSourceId(null);
    }
  };

  const handleFetchAll = async () => {
    await triggerFetchAll.mutateAsync();
  };

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return 'Never';
    return new Date(dateStr).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  };

  const groupedSources = sourcesData?.sources.reduce((acc, source) => {
    const region = source.region;
    if (!acc[region]) {
      acc[region] = [];
    }
    acc[region].push(source);
    return acc;
  }, {} as Record<string, DataSource[]>) || {};

  return (
    <div className="space-y-8">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-serif font-semibold text-font-primary">Settings</h1>
          <p className="text-font-secondary">Manage data sources and application settings</p>
        </div>
        <Button
          variant="primary"
          onClick={handleFetchAll}
          isLoading={triggerFetchAll.isPending}
        >
          <RefreshCw className="w-4 h-4 mr-2" />
          Fetch All Sources
        </Button>
      </div>

      {/* Data Sources */}
      <div className="space-y-6">
        <h2 className="text-lg font-semibold text-font-primary flex items-center">
          <Database className="w-5 h-5 mr-2" />
          Data Sources
        </h2>

        {isLoading && (
          <div className="text-center py-8">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-4 border-brand-primary border-r-transparent" />
          </div>
        )}

        {Object.entries(groupedSources).map(([region, sources]) => (
          <div key={region} className="space-y-3">
            <h3 className="text-sm font-semibold uppercase tracking-wide text-font-secondary">
              {region}
            </h3>
            <div className="grid gap-4">
              {sources.map((source) => (
                <div
                  key={source.id}
                  className="card p-4 flex items-center justify-between"
                >
                  <div className="flex items-center space-x-4">
                    <div
                      className={`w-3 h-3 rounded-full ${
                        source.active ? 'bg-green-500' : 'bg-gray-400'
                      }`}
                    />
                    <div>
                      <h4 className="font-medium text-font-primary">{source.name}</h4>
                      <p className="text-sm text-font-secondary">{source.source_type}</p>
                    </div>
                  </div>

                  <div className="flex items-center space-x-4">
                    <div className="text-right">
                      <div className="flex items-center space-x-1 text-sm text-font-secondary">
                        <Clock className="w-4 h-4" />
                        <span>Last fetch: {formatDate(source.last_fetch_at)}</span>
                      </div>
                    </div>

                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleFetchSource(source.id)}
                      isLoading={fetchingSourceId === source.id}
                      disabled={!source.active}
                    >
                      <RefreshCw className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      {/* Fetch Status */}
      {(triggerFetch.isSuccess || triggerFetchAll.isSuccess) && (
        <div className="card p-4 bg-green-50 border-green-200">
          <div className="flex items-center space-x-2 text-green-800">
            <CheckCircle className="w-5 h-5" />
            <span>Data fetch completed successfully!</span>
          </div>
        </div>
      )}

      {(triggerFetch.isError || triggerFetchAll.isError) && (
        <div className="card p-4 bg-red-50 border-red-200">
          <div className="flex items-center space-x-2 text-red-800">
            <AlertCircle className="w-5 h-5" />
            <span>Failed to fetch data. Please try again.</span>
          </div>
        </div>
      )}

      {/* Schedule Info */}
      <div className="card p-6">
        <h2 className="text-lg font-semibold text-font-primary mb-4">Automatic Fetching</h2>
        <p className="text-font-secondary mb-4">
          Data is automatically fetched from all active sources every week on Monday at 6:00 AM EST.
        </p>
        <div className="flex items-center space-x-2 text-sm text-font-secondary">
          <Clock className="w-4 h-4" />
          <span>Schedule: Weekly (Monday 6:00 AM EST)</span>
        </div>
      </div>
    </div>
  );
}
